<template>
  <div class="page-wrapper">
    <!-- Tabs Wrapper -->
    <div class="tabs-wrapper">
      <div class="tabs-header">
        <b-tabs lazy>
          <b-tab :title="$t('globals.all_permissions')" active></b-tab>
        </b-tabs>
      </div>
      <div class="tab-content">
        <div class="tab-content-inner">
          <!-- Search and Filters Section -->
          <div class="filters-section">
            <div class="search-container">
              <label class="search-label">{{ $t("globals.search") }}</label>
              <b-form-input
                v-model="filter"
                class="search-input"
                :placeholder="$t('globals.type_to_search')"
              />
            </div>

            <div class="action-buttons">
              <button
                class="reload-btn"
                @click="resetReload"
                :disabled="loading"
                v-b-tooltip.hover
                :title="$t('globals.clear_filters')"
              >
                <icon name="sync-alt" :spin="loading"></icon>
              </button>
            </div>
          </div>

          <!-- Table -->
          <div class="table-wrapper">
            <table class="modern-table">
              <thead>
                <tr>
                  <th
                    v-for="field in fields"
                    :key="field.key"
                    :class="{ sortable: field.sortable }"
                    @click="field.sortable && handleSort(field.key)"
                  >
                    {{ field.label }}
                    <span v-if="sortBy === field.key" class="sort-icon">
                      {{ sortDesc ? '▼' : '▲' }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in paginatedItems"
                  :key="item.id"
                  class="table-row"
                  @click="rowClicked(item)"
                >
                  <td>
                    <span class="customer-link" style="cursor: pointer;">
                      {{ item.name }}
                    </span>
                  </td>
                  <td>{{ item.email }}</td>
                  <td>
                    <div class="brands-container">
                      <img
                        v-if="hasBrand(item, 'ulubv')"
                        class="brand-logo"
                        src="@/assets/ulubv_logo.png"
                        alt="ULU BV"
                        title="ULU BV"
                      />
                      <img
                        v-if="hasBrand(item, 'ulu')"
                        class="brand-logo"
                        src="@/assets/uluLogo.png"
                        alt="ULU"
                        title="ULU"
                      />
                      <img
                        v-if="hasBrand(item, 'fleetify')"
                        class="brand-logo"
                        src="@/assets/fleetifyLogo.png"
                        alt="Fleetify"
                        title="Fleetify"
                      />
                      <img
                        v-if="hasBrand(item, 'uluuk')"
                        class="brand-logo"
                        src="@/assets/uluuk_logo.png"
                        alt="ULU UK"
                        title="ULU UK"
                      />
                      <img
                        v-if="hasBrand(item, 'cartracker')"
                        class="brand-logo"
                        src="@/assets/cartrackerLogo.png"
                        alt="Cartracker"
                        title="Cartracker"
                      />
                      <img
                        v-if="hasBrand(item, 'traksi')"
                        class="brand-logo"
                        src="@/assets/traksi_logo.png"
                        alt="Traksi"
                        title="Traksi"
                      />
                      <img
                        v-if="hasBrand(item, 'requre')"
                        class="brand-logo"
                        src="@/assets/requre_logo.png"
                        alt="Requre"
                        title="Requre"
                      />
                      <img
                        v-if="hasBrand(item, 'finnik')"
                        class="brand-logo"
                        src="@/assets/finnik_logo.png"
                        alt="Finnik"
                        title="Finnik"
                      />
                      <img
                        v-if="hasBrand(item, 'national_netherlands')"
                        class="brand-logo"
                        src="@/assets/ohra_logo.png"
                        alt="National Netherlands"
                        title="National Netherlands"
                      />
                      <img
                        v-if="hasBrand(item, 'imoto')"
                        class="brand-logo"
                        src="@/assets/imoto_logo.png"
                        alt="iMoto"
                        title="iMoto"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="loading" class="loading-state">
              <div class="spinner"></div>
              <p>{{ $t("globals.loading") }}...</p>
            </div>
            <div v-else-if="filteredItems.length === 0" class="empty-state">
              <p>{{ $t("globals.no_results") }}</p>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-section">
            <div class="controls-left">
              <span class="summary-label">{{ $t("globals.per_page") }}</span>
              <b-form-select
                v-model="perPage"
                :options="perPageOptions"
                class="per-page-select"
              />
              <span class="summary-label" style="margin-left: 1rem;">
                {{ $t("globals.showing") }}
                {{ ((currentPage - 1) * perPage) + 1 }}-{{ Math.min(currentPage * perPage, filteredItems.length) }}
                {{ $t("globals.of") }} {{ filteredItems.length }}
              </span>
            </div>
            <div class="pagination-controls">
              <button
                class="page-btn"
                :disabled="currentPage === 1"
                @click="currentPage = 1"
              >
                &lt;&lt;
              </button>
              <button
                class="page-btn"
                :disabled="currentPage === 1"
                @click="currentPage--"
              >
                &lt;
              </button>
              <span style="padding: 0 1rem; color: #6c757d; font-size: 0.875rem;">
                {{ $t("globals.page") }} {{ currentPage }} {{ $t("globals.of") }} {{ totalPages }}
              </span>
              <button
                class="page-btn"
                :disabled="currentPage >= totalPages"
                @click="currentPage++"
              >
                &gt;
              </button>
              <button
                class="page-btn"
                :disabled="currentPage >= totalPages"
                @click="currentPage = totalPages"
              >
                &gt;&gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";

import "vue-awesome/icons/sync-alt";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
  },
  data() {
    return {
      filter: "",
      currentPage: 1,
      perPage: 20,
      sortBy: "name",
      sortDesc: false,
      loading: false,
      perPageOptions: [
        { text: "20", value: 20 },
        { text: "40", value: 40 },
        { text: "60", value: 60 }
      ],
      fields: [
        {
          key: "name",
          label: this.$t("globals.name"),
          sortable: true
        },
        {
          key: "email",
          label: this.$t("globals.email"),
          sortable: true
        },
        {
          key: "brands",
          label: this.$t("globals.brands"),
          sortable: false
        }
      ],
      items: []
    };
  },
  computed: {
    ...mapGetters(["getUserBrandPermissions"]),
    filteredItems() {
      if (!this.filter) {
        return this.sortedItems;
      }
      const searchTerm = this.filter.toLowerCase();
      return this.sortedItems.filter(item => {
        return (
          (item.name && item.name.toLowerCase().includes(searchTerm)) ||
          (item.email && item.email.toLowerCase().includes(searchTerm))
        );
      });
    },
    sortedItems() {
      const items = [...this.items];
      if (!this.sortBy) {
        return items;
      }

      return items.sort((a, b) => {
        const aVal = a[this.sortBy];
        const bVal = b[this.sortBy];

        if (aVal === bVal) return 0;

        let result = 0;
        if (aVal === null || aVal === undefined) {
          result = 1;
        } else if (bVal === null || bVal === undefined) {
          result = -1;
        } else {
          result = aVal < bVal ? -1 : 1;
        }

        return this.sortDesc ? -result : result;
      });
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredItems.length / this.perPage) || 1;
    }
  },
  methods: {
    ...mapActions("axios_mod", ["simpleFetch"]),
    ...mapMutations(["setShowLoadingModal"]),
    handleSort(fieldKey) {
      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
    },
    hasBrand(item, brandName) {
      return item.permissions.filter(permission => permission.brand === brandName).length > 0;
    },
    rowClicked(item) {
      // Don't navigate if user is selecting text
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      this.$router.push({ name: "editpermissions", params: { id: item.id } });
    },
    fetchAllUsers() {
      const data = {
        query: `
          query {
            allAuthenticatedUsers {
              id
              firstname
              lastname
              email
              user_permissions {
                brand
              }
            }
          }
        `
      };

      this.loading = true;
      this.simpleFetch({data, this_ref: this}).then((res) => {
        if (res) {
          this.items = res.allAuthenticatedUsers.map(user => {
            return {
              id: user.id,
              name: `${user.firstname} ${user.lastname}`,
              email: user.email,
              permissions: user.user_permissions
            };
          });
        }
      }).finally(() => {
        this.loading = false;
      });
    },
    resetReload() {
      this.filter = "";
      this.currentPage = 1;
      this.sortBy = "name";
      this.sortDesc = false;
      this.fetchAllUsers();
    }
  },
  watch: {
    filter() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.fetchAllUsers();
  },
  created() {
    if (!this.getUserBrandPermissions.permissions_index) {
      this.$router.push({ name: "dashboard" });
    }
  }
};
</script>

<style scoped>
.brands-container {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.brand-logo {
  height: 1.5rem;
  object-fit: contain;
}
</style>
